import { VersionType, GetUtsvOperationalMetricsForPropertyYearQuery, OriginalRevenueOperationalMetricValidKey } from '../../../../__generated__/generated_types';
import { VZHotTableConfig } from "../../../../components/vz-ui/vz-hot-table/vz-hot-table-config";
import { IVZHotTableRow, TVZHotTableSummaryFunction } from "../../../../components/vz-ui/vz-hot-table/VZHotTable";
import {Property, propertyDisplayBudgetYear} from '../../../../contexts/properties/logic';


export type utsvOpMetricQueryReturn = GetUtsvOperationalMetricsForPropertyYearQuery["originalRevenueUTSVOperationalMetricsForPropertyYear"];

export const versionLabelMap = new Map<VersionType, string>([
    [VersionType.Actuals, "Actuals"],
    [VersionType.Reforecast, "Reforecast"],
    [VersionType.Budget, "Budget"],
]);

export function getVersionLabel(versionType: VersionType, year: number, property: Property): string {
    let label = `${versionLabelMap.get(versionType)} ${year}`;

    if (property.isAcquisitionProperty) {
        if (year == property.reforecastYear) {
            label = "Year 1";
        } else if (year == property.reforecastYear - 1) {
            label = "T12";
        } else {
            label = String(propertyDisplayBudgetYear(property));
        }
    }

    return label;
}

export interface IUTSVUnitType {
    unitTypeId: string;
    unitTypeName: string;
    unitTypeCount: number;
    unitTypeCountMonthly: number[];
    renewalRate: number[];
    renewalRatio: number[];
    moveOutRate: number[];
    earlyTerminationCount: number[];
    moveOutCount: number[];
    leaseExpirationCount: number[];
    renewalCount: number[];
    monthToMonthCount: number[];
    monthToMonthMoveOutCount: number[];
    averageMarketRent: number[];
    occupancyRate: number[];
    userSpecifiedUnitCount: number[];
    moveInCount: number[];
}

export interface IUTSVVersion {
    year: number;
    version: VersionType;
    unitTypes: IUTSVUnitType[];
}

// converts our decimal to number, keeping original precision
function decimalToPercent(value: string | null): number {
    if (value == null) {
        return 0;
    }

    const percent = parseFloat(value) * 100;
    const fractional = value.split(".")[1];
    let precision = 0;

    if (fractional) {
        precision = fractional.length - 2;
    }

    return +percent.toFixed(precision < 0 ? 0 : precision);
}

// converts the decimals from the api response from strings to numbers for calculations
export function parseUTSVQueryResponse(data: utsvOpMetricQueryReturn): IUTSVVersion[] {
    const ret: IUTSVVersion[] = [];

    for (const version of data.sortByDescending("year")) {
        // if we don't have any unit types, nothing to calculate
        if (!version.unitTypes || version.unitTypes.length == 0) {
            return [];
        }

        const parsedVersion = {
            version: version.version,
            year: version.year,
            unitTypes: version.unitTypes.sortBy("unitTypeName").map(unitType => ({
                ...unitType,
                unitTypeCountMonthly: unitType.unitTypeCountMonthly.map(x => +(x ?? 0)),
                renewalRate: unitType.renewalRate.map(x => +(x ?? 0)),
                renewalRatio: unitType.renewalRatio.map(x => decimalToPercent(x)),
                moveOutRate: unitType.moveOutRate.map(x => decimalToPercent(x)),
                earlyTerminationCount: unitType.earlyTerminationCount.map(x => +(x ?? 0)),
                moveOutCount: unitType.moveOutCount.map(x => +(x ?? 0)),
                leaseExpirationCount: unitType.leaseExpirationCount.map(x => +(x ?? 0)),
                renewalCount: unitType.renewalCount.map(x => +(x ?? 0)),
                monthToMonthCount: unitType.monthToMonthCount.map(x => +(x ?? 0)),
                monthToMonthMoveOutCount: unitType.monthToMonthMoveOutCount.map(x => +(x ?? 0)),
                averageMarketRent: unitType.averageMarketRent.map(x => +(x ?? 0)),
                occupancyRate: unitType.occupancyRate.map(x => decimalToPercent(x)),
                userSpecifiedUnitCount: unitType.userSpecifiedUnitCount.map(x => +(x ?? 0)),
                // move ins are stubbed out here so we can calculate them later when needed
                moveInCount: Array(12).fill(0),
            })),
        };

        ret.push(parsedVersion);
    }

    return ret;
}

function summarizeRenewalRates(valuesForMonth: number[], monthIndex: number, unitTypes: IUTSVUnitType[]): number {
    const expirationsForMonth = unitTypes.map(x => x.leaseExpirationCount[monthIndex] ?? 0);
    const renewalRatiosForMonth = unitTypes.map(x => (x.renewalRatio[monthIndex] ?? 0) / 100);
    const weights = expirationsForMonth.map((x, index) => x * (renewalRatiosForMonth[index] ?? 0));
    const weightedPercentages = getWeightedPercent(weights, weights).map(x => x / 100);
    const tradeouts = valuesForMonth.map((x, index) => (x ?? 0) * (weightedPercentages[index] ?? 0));

    return +tradeouts.sum().toFixed(2);
}

function percentToCount(percentages: (number | null)[], counts: number[]): number[] {
    return percentages.map((x, i) => (x ?? 0) * (counts[i] ?? 0));
}

function getWeightedPercent(counts: number[], weights: number[]): number[] {
    const totalWeight = weights.sum();
    if (totalWeight > 0) {
        return counts.map(x => x / totalWeight * 100);
    }

    return new Array(counts.length).fill(0);
}

function summarizeBySingleWeight(values: number[], totals: number[]): number {
    const counts = percentToCount(values.map(x => (x ?? 0) / 100), totals);
    const weightedPercentages = getWeightedPercent(counts, totals);
    return +weightedPercentages.sum().toFixed(2);
}

export function buildUTSVTableData(
    data: utsvOpMetricQueryReturn,
    metric: OriginalRevenueOperationalMetricValidKey,
    versionType: VersionType,
    property: Property,
): IVZHotTableRow[] {
    const tableData: IVZHotTableRow[] = [];
    const typedData: IUTSVVersion[] = parseUTSVQueryResponse(data);

    function valueForMonth(unitType: IUTSVUnitType, monthIndex: number): number {
        if (
            metric === OriginalRevenueOperationalMetricValidKey.UserSpecifiedUnitCount &&
            versionType === VersionType.Reforecast &&
            monthIndex < property.reforecastStartMonthIndex
        ) {
            return unitType.unitTypeCountMonthly[monthIndex] ?? 0;
        }

        return unitType[metric][monthIndex] ?? 0;
    }

    for (const versionData of typedData) {
        if (versionType == VersionType.Reforecast && versionData.version == VersionType.Budget) {
            continue;
        }

        // this part is for how the version summary average or total is calculated for each month
        const unitCounts = versionData.unitTypes.map(x => x.unitTypeCount);
        const summaryValues: number[] = [];
        for (let i = 0; i < 12; i++) {
            const valuesForMonth = versionData.unitTypes.map(x => valueForMonth(x, i));
            const expirationsForMonth = versionData.unitTypes.map(x => x.leaseExpirationCount[i] ?? 0);

            switch (metric) {
                case OriginalRevenueOperationalMetricValidKey.RenewalRate: {
                    summaryValues.push(summarizeRenewalRates(valuesForMonth, i, versionData.unitTypes));
                    break;
                }
                case OriginalRevenueOperationalMetricValidKey.AverageMarketRent:
                    summaryValues.push(+valuesForMonth.average().toFixed(2));
                    break;
                case OriginalRevenueOperationalMetricValidKey.OccupancyRate: {
                    summaryValues.push(summarizeBySingleWeight(valuesForMonth, unitCounts));
                    break;
                }
                case OriginalRevenueOperationalMetricValidKey.MoveOutRate:
                case OriginalRevenueOperationalMetricValidKey.RenewalRatio: {
                    summaryValues.push(summarizeBySingleWeight(valuesForMonth, expirationsForMonth));
                    break;
                }
                case OriginalRevenueOperationalMetricValidKey.UserSpecifiedUnitCount:
                    summaryValues.push(valuesForMonth.sum());
                    break;
            }
        }

        // we dont want to render actuals rows that have no meaningful data
        if (versionData.version == VersionType.Actuals && summaryValues.every(x => x == 0)) {
            continue;
        }

        const summaryRowData: IVZHotTableRow = {
            rowId: "summary-row",
            label: getVersionLabel(versionData.version, versionData.year, property),
            rowType: versionData.version == versionType ? "summary" : "historical",
            values: summaryValues,
        };

        switch (metric) {
            case OriginalRevenueOperationalMetricValidKey.RenewalRate: {
                summaryRowData.parentRowSummaryFunction = (_row, monthIndex, values) => {
                    return summarizeRenewalRates(values, monthIndex, versionData.unitTypes);
                };
                break;
            }
            case OriginalRevenueOperationalMetricValidKey.OccupancyRate: {
                summaryRowData.parentRowSummaryFunction = (_row, _monthIndex, values) => {
                    return summarizeBySingleWeight(values, unitCounts);
                };
                break;
            }
            case OriginalRevenueOperationalMetricValidKey.MoveOutRate:
            case OriginalRevenueOperationalMetricValidKey.RenewalRatio: {
                summaryRowData.parentRowSummaryFunction = (_row, monthIndex, values) => {
                    const expirationsForMonth = versionData.unitTypes.map(x => x.leaseExpirationCount[monthIndex] ?? 0);
                    return summarizeBySingleWeight(values, expirationsForMonth);
                };
                break;
            }
        }

        tableData.push(summaryRowData);

        if (versionData.version == versionType) {
            for (const unitType of versionData.unitTypes) {
                const values = Array.from({length: 12}, (_, monthIndex) => valueForMonth(unitType, monthIndex));

                tableData.push({
                    label: unitType.unitTypeName,
                    rowType: "child",
                    parentRowId: "summary-row",
                    values,
                });
            }
        }
    }

    return tableData;
}

export function buildUTSVTableConfig(
    data: IVZHotTableRow[],
    tableLocked: boolean,
    versionType: VersionType,
    reforecastStartMonthIndex: number,
    format: "currency" | "percent" | "count",
    summaryFunction: TVZHotTableSummaryFunction,
    numRowHeaders = 1,
    numSummaryRows = 1
): VZHotTableConfig {
    const rowHeadersOffset = numRowHeaders - 1;
    const tableConfig = new VZHotTableConfig();

    tableConfig.tableSummaryFunction = summaryFunction;
    for(let i = 0; i < numSummaryRows; i++) {
        tableConfig.applyToRow(i, { isBoldText: true });
    }
    tableConfig.applyToColumn(0, {isRowHeader: true, isBoldText: true});
    tableConfig.applyToColumnAndFollowing(numRowHeaders, {format});
    tableConfig.applyToColumn(13 + rowHeadersOffset, {isGreyBackground: true, isBoldText: true});
    tableConfig.push({
        rowStart: 0,
        rowEnd: data.filter(x => x.rowType != "historical").length - 1,
        colStart: (versionType == VersionType.Reforecast ? reforecastStartMonthIndex + 1 : 1) + rowHeadersOffset,
        colEnd: 12 + rowHeadersOffset,
        config: {
            isReadOnly: tableLocked,
        },
    });

    if (versionType == VersionType.Reforecast) {
        tableConfig.push({
            rowStart: 0,
            rowEnd: data.filter(x => x.rowType != "historical").length - 1,
            colStart: reforecastStartMonthIndex + 1 + rowHeadersOffset,
            colEnd: reforecastStartMonthIndex + 1 + rowHeadersOffset,
            config: {
                isThickBorderLeft: true,
            },
        });
    }

    const firstHistoricalRow = data.find(x => x.rowType == "historical");

    if (firstHistoricalRow) {
        tableConfig.applyToRow(data.indexOf(firstHistoricalRow), {isThickBorderTop: true});
        tableConfig.applyToRowAndFollowing(data.indexOf(firstHistoricalRow), {isGreyBackground: true, isBoldText: true});
    }

    return tableConfig;
}
